import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthLoadingView } from 'shared-ui/lib/components/auth/AuthLoadingView';
import { Button } from 'shared-ui/lib/components/Button';
import { Image } from 'shared-ui/lib/components/Image';
import { VehicleName } from 'shared-ui/lib/components/VehicleName';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { formatDate } from 'shared-ui/lib/utils/format-date';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { routesConfig } from '../../config/routes.config';
import { buildServiceCallDashboardHref } from '../../form/service-call-dashboard-filter/service-call-dashboard-filter.form';
import {
  useMarkPartnerDashboardFaultReadMutation,
  useMarkPartnerDashboardFaultUnreadMutation,
  usePartnerDashboardFaultQuery,
} from '../../generated/graphql';

const actionButtonClassName =
  'w-full md:w-auto md:float-right md:mr-2 py-2 px-4 border transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:bg-gray-300';

export const FaultShowScene = (): JSX.Element => {
  const { t } = useTranslation('FaultShowScene');
  const { id } = useParams<{ id: string }>();
  const { data, loading, refetch } = usePartnerDashboardFaultQuery(setupQueryConfig({ id }));
  const [markAsRead, { loading: markAsReadLoading }] = useMarkPartnerDashboardFaultReadMutation({
    variables: { id },
    onCompleted: (result) => {
      if (result.markPartnerDashboardFaultRead) {
        toast.success(t('toastMarkedAsRead'));
        refetch();
      }
    },
  });
  const [markAsUnread, { loading: markAsUnreadLoading }] =
    useMarkPartnerDashboardFaultUnreadMutation({
      variables: { id },
      onCompleted: (result) => {
        if (result.markPartnerDashboardFaultUnread) {
          toast.success(t('toastMarkedAsUnread'));
          refetch();
        }
      },
    });

  if (loading || !data) return <AuthLoadingView />;

  const { partnerDashboardFault: fault } = data;

  return (
    <div className="mx-auto w-full max-w-3xl sm:px-2">
      <div className="bg-white py-8 px-6 sm:px-10 shadow rounded-lg">
        <div className="mt-6">
          <div className="max-w-screen-lg mt-6 mx-auto mb-4">
            <Image src={fault.photo} className="mx-auto rounded-md" w={672} />
          </div>
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('businessName')}</p>
            <p className="text-lg text-gray-color">{fault.businessName}</p>
          </div>
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('vehicle')}</p>
            <p className="text-lg text-gray-color">
              <VehicleName vehicle={fault.vehicle} />
            </p>
          </div>
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('userName')}</p>
            <p className="text-lg text-gray-color">{fault.userName}</p>
          </div>
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('createdAt')}</p>
            <p className="text-lg text-gray-color">{formatDate(fault.createdAt)}</p>
          </div>
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('status')}</p>
            <p className="text-lg text-gray-color">
              {fault.isDone ? t('statusDone') : t('statusOpen')}
            </p>
          </div>
          <div className="grid grid-cols-1 pb-3">
            <p className="text-lg text-gray-900">{t('description')}</p>
            <p className="text-md mt-4 text-gray-color">{fault.description}</p>
          </div>
        </div>

        <div className="space-y-2 md:space-y-0">
          <Link
            className="block md:inline-block md:float-right text-center py-2 px-4 border transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:bg-gray-300"
            to={routesConfig.VEHICLES.SHOW.replace(':id', fault.vehicleId)}
          >
            {t('vehicleLink')}
          </Link>
          {fault.isRead === false ? (
            <Button
              type="button"
              loading={markAsReadLoading}
              onClick={markAsRead}
              className={actionButtonClassName}
            >
              {t('markAsRead')}
            </Button>
          ) : (
            <Button
              type="button"
              loading={markAsUnreadLoading}
              onClick={markAsUnread}
              className={actionButtonClassName}
            >
              {t('markAsUnread')}
            </Button>
          )}
          <Link
            className="block md:inline-block text-center py-2 px-4 border transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:bg-gray-300"
            to={buildServiceCallDashboardHref(routesConfig.SERVICE_CALL.DASHBOARD, 'faults')}
          >
            {t('backLink')}
          </Link>
        </div>
      </div>
    </div>
  );
};
